import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Space, Typography } from "antd";
import { agentPlatformApi, AgentPlatformApiError, Evaluation as Metrics } from "./api";

const { Text } = Typography;

export function EvaluationFeedback({ workspaceId, targetType, targetId }: {
  workspaceId: string; targetType: "brief" | "issue"; targetId: string;
}): React.ReactElement {
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const epoch = useRef(0);
  const requests = useRef(new Map<string, string>());
  useEffect(() => {
    epoch.current += 1; requests.current.clear(); setPending(false); setMessage(undefined); setError(undefined);
    return () => { epoch.current += 1; };
  }, [workspaceId, targetType, targetId]);
  async function submit(value: string) {
    if (pending) return;
    const current = epoch.current;
    const idempotencyKey = requests.current.get(value) || `evaluation-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    requests.current.set(value, idempotencyKey);
    setPending(true); setError(undefined); setMessage(undefined);
    try {
      await agentPlatformApi.evaluationFeedback(workspaceId, { targetType, targetId, value, idempotencyKey });
      if (current !== epoch.current) return;
      requests.current.delete(value); setMessage("Feedback saved. Thank you.");
    } catch (err) {
      if (current !== epoch.current) return;
      if (err instanceof AgentPlatformApiError && err.status === 409) requests.current.delete(value);
      setError("Feedback could not be saved. Retry the same choice.");
    } finally { if (current === epoch.current) setPending(false); }
  }
  if (workspaceId !== "private-household") return <></>;
  return <details className="agent-platform-brief-details">
    <summary>Give feedback</summary>
    <Space direction="vertical" size="small">
      <Text type="secondary">Your rating helps evaluate these checks; it does not change the issue’s status.</Text>
      <Space wrap>
        <Button size="small" disabled={pending} onClick={() => void submit("useful")}>Useful</Button>
        <Button size="small" disabled={pending} onClick={() => void submit("not_useful")}>Not useful</Button>
        <Button size="small" disabled={pending} onClick={() => void submit(targetType === "issue" ? "false_alert" : "missed_issue")}>
          {targetType === "issue" ? "False alert" : "Missed something"}
        </Button>
      </Space>
      {message && <Text role="status">{message}</Text>}
      {error && <Alert type="error" message={error} />}
    </Space>
  </details>;
}

export default function Evaluation({ workspaceId, refreshToken = 0 }: {
  workspaceId: string; refreshToken?: number;
}): React.ReactElement {
  const [metrics, setMetrics] = useState<Metrics>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const controller = new AbortController(); let current = true;
    setMetrics(undefined); setError(undefined);
    if (workspaceId !== "private-household") return;
    setLoading(true);
    agentPlatformApi.evaluation(workspaceId, controller.signal).then((data) => {
      if (current && data.enabled) setMetrics(data);
    }).catch((err) => {
      if (current && !(err instanceof AgentPlatformApiError && err.status === 404)) setError("Evaluation could not be loaded.");
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; controller.abort(); };
  }, [workspaceId, refreshToken, refresh]);
  if (workspaceId !== "private-household" || (!loading && !error && !metrics)) return <></>;
  return <details className="agent-platform-brief-details">
    <summary>Local pilot evaluation</summary>
    <Space direction="vertical" className="agent-platform-full">
      <Text>Real follow-up quality is still being evaluated. Use Give feedback on an issue or brief to rate it.</Text>
      <Button disabled={loading} onClick={() => setRefresh((value) => value + 1)}>Refresh evaluation</Button>
      {loading && <Text>Loading evaluation…</Text>}
      {error && <Alert type="error" message={error} />}
      {metrics && <>
        <Text type="secondary">Bounded sample of accessible Household records, not a complete account history.</Text>
        <Text>{metrics.runs.succeeded || 0} checks succeeded; {metrics.runs.failed || 0} failed; {metrics.runs.followup || 0} follow-up checks.</Text>
        <Text>{metrics.issues.discovered || 0} issues discovered; {metrics.issues.followedUp || 0} revisited; {metrics.issues.resolved || 0} resolved. {metrics.issues.lifecycleCompleted || 0} completed a discovery, recheck and resolution cycle.</Text>
        <Text>Reported usage: {metrics.usage.inputTokens.toLocaleString()} input and {metrics.usage.outputTokens.toLocaleString()} output tokens. Usage unavailable for {metrics.usage.unknownRuns} runs.</Text>
        <Text>{metrics.reliability.explicitToolDenialRuns} tool requests denied; {metrics.reliability.unclassifiedFailedRuns} other failed checks; {metrics.reliability.unchangedReminders} unchanged reminders.</Text>
        <Text>Owner ratings: {metrics.ownerLabels.useful || 0} useful; {metrics.ownerLabels.notUseful || 0} not useful; {metrics.ownerLabels.falseAlert || 0} false alerts; {metrics.ownerLabels.missedIssue || 0} missed-item reports.</Text>
        <Text type="secondary">Unrated items have no accuracy verdict. Token totals are not a dollar-cost estimate.</Text>
      </>}
    </Space>
  </details>;
}
